import type { AppliedRegulation } from 'features/ducted-compatibility/ducted-compatibility-slice';

// The cart's regulation codes for the two furnace NOx tiers. Ultra-Low NOx
// supersedes Low NOx: in an ultra district only ultra-low furnaces qualify,
// so the Low NOx label would add no information (the PartnerNet project page
// applies the same rule to its badges).
const LOW_NOX_CODE = 'lo_nox';
const ULTRA_LOW_NOX_CODE = 'ultra_nox';

export type RegulationLocationLike = {
  city?: string | null;
  state?: string | null;
  zipcode?: string | null;
};

// Distinct applied regulations across every in-play result set, first-seen
// order, with Low NOx dropped whenever Ultra-Low NOx is also in force.
export function distinctAppliedRegulations(
  regulations: readonly AppliedRegulation[]
): AppliedRegulation[] {
  const byCode = new Map<string, AppliedRegulation>();
  regulations.forEach((regulation) => {
    if (!byCode.has(regulation.code)) {
      byCode.set(regulation.code, regulation);
    }
  });
  if (byCode.has(ULTRA_LOW_NOX_CODE)) {
    byCode.delete(LOW_NOX_CODE);
  }
  return Array.from(byCode.values());
}

// "Low NOx Furnaces", "Low NOx Furnaces and California Approved PTAC",
// "A, B, and C" — the labels as the admin named them, joined for a sentence.
export function joinRegulationLabels(
  regulations: readonly AppliedRegulation[]
): string {
  const labels = regulations.map((regulation) => regulation.label);
  if (labels.length <= 1) return labels.join('');
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

// "Los Angeles, CA", else "CA", else the ZIP, else "your location".
export function describeRegulationLocation(
  location: RegulationLocationLike | null | undefined
): string {
  const city = location?.city?.trim();
  const state = location?.state?.trim();
  const zipcode = location?.zipcode?.trim();
  if (city && state) return `${city}, ${state}`;
  if (state) return state;
  if (zipcode) return zipcode;
  return 'your location';
}
